import { parseScopeDimToSelection } from 'hooks/ppa/ppaScope.helpers';
import type { WhitespaceGridCell, WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';
import { formatWhitespaceCellKpiValue } from 'hooks/useWhitespaceAnalysisGridData';

type ScopeFilterContext = {
    ppaPersistedScope?: Record<string, unknown> | null;
    allCheckedAttribute?: Record<string, Record<string, unknown>>;
    timeSelectionLabel?: string;
    timePeriodSelection?: string;
};

const WORKFLOW_NAME = 'Whitespace Analysis';

const getFirstCheckedAttributeValue = (
    allCheckedAttribute: Record<string, Record<string, unknown>> | undefined,
    attributeTypes: string[],
) => {
    if (!allCheckedAttribute) return undefined;
    const normalizedTargets = new Set(attributeTypes.map((type) => type.toLowerCase()));
    const matchedEntry = Object.entries(allCheckedAttribute).find(([attributeType]) =>
        normalizedTargets.has(attributeType.toLowerCase()),
    );
    if (!matchedEntry) return undefined;
    const [, selectedValues] = matchedEntry;
    return Object.keys(selectedValues ?? {})[0];
};

export const buildWhitespaceCellId = (xAxisValue: string, yAxis: string, yAxisBucket: string) =>
    `${xAxisValue || '-'}_${yAxis}_${yAxisBucket || '-'}`;

const VALID_ENTRY_POINTS = ['ppa_home', 'ppa_navigation', 'direct'] as const;
type EntryPoint = (typeof VALID_ENTRY_POINTS)[number];

export const getWhitespaceEntryPoint = (): EntryPoint => {
    const referrer = document.referrer || '';
    if (referrer.includes('/ppa/home')) return 'ppa_home';
    if (referrer.includes('/ppa/')) return 'ppa_navigation';
    const stored = localStorage.getItem('from');
    return VALID_ENTRY_POINTS.includes(stored as EntryPoint) ? (stored as EntryPoint) : 'direct';
};

export const getWhitespaceComparisonMode = (gridRows: WhitespaceGridRow[], columnHeaders: string[]) => {
    for (const row of gridRows) {
        for (const columnHeader of columnHeaders) {
            const cell = row.cells[columnHeader];
            if (cell?.comparisonPeriod) {
                return cell.comparisonPeriod;
            }
        }
    }
    return '-';
};

export const computeWhitespaceGridMetrics = (gridRows: WhitespaceGridRow[], columnHeaders: string[]) => {
    let visibleCellCount = 0;
    let opportunityCellCount = 0;
    let totalEntityCount = 0;

    gridRows.forEach((row) => {
        columnHeaders.forEach((columnHeader) => {
            const cell = row.cells[columnHeader];
            if (!cell) return;
            visibleCellCount += 1;
            if (cell.isWhitespace) {
                opportunityCellCount += 1;
            }
            totalEntityCount += (cell.items?.length ?? 0) + (cell.moreCount ?? 0);
        });
    });

    const totalCellCount = gridRows.length * columnHeaders.length;

    return {
        total_cell_count: totalCellCount,
        visible_cell_count: visibleCellCount,
        opportunity_cell_count: opportunityCellCount,
        total_entity_count: totalEntityCount,
        row_bucket_count: gridRows.length,
        column_count: columnHeaders.length,
        generated_bucket_count: gridRows.length,
    };
};

export const buildWhitespaceMixpanelBaseProps = ({
    ppaPersistedScope,
    allCheckedAttribute,
    timeSelectionLabel,
    timePeriodSelection,
}: ScopeFilterContext) => {
    const market =
        parseScopeDimToSelection(ppaPersistedScope?.dim2) ??
        getFirstCheckedAttributeValue(allCheckedAttribute, ['market', 'channel', 'dim2']) ??
        '-';
    const category =
        parseScopeDimToSelection(ppaPersistedScope?.dim1) ??
        getFirstCheckedAttributeValue(allCheckedAttribute, ['category', 'dim1']) ??
        '-';

    let timePeriod = timeSelectionLabel?.trim() || '-';
    if (timePeriod === '-') {
        try {
            const parsed = JSON.parse(timePeriodSelection ?? '{}');
            if (parsed?.start_timestamp && parsed?.end_timestamp) {
                timePeriod = `${parsed.start_timestamp}-${parsed.end_timestamp}`;
            }
        } catch {
            // ignore malformed payloads
        }
    }

    return {
        workflow_name: WORKFLOW_NAME,
        market,
        category,
        time_period: timePeriod,
    };
};

export const getWhitespaceCellHoverProps = (
    cell: WhitespaceGridCell,
    rowLabel: string,
    columnHeader: string,
    yAxis: string,
) => ({
    cell_id: buildWhitespaceCellId(columnHeader, yAxis, rowLabel),
    x_axis_value: columnHeader,
    y_axis_bucket: rowLabel,
    total_value: cell.raw_value ?? cell.valueSales ?? '-',
    growth_pct: typeof cell.trendValue === 'number' ? cell.trendValue : '-',
    share_pct: formatWhitespaceCellKpiValue(cell.ownShareKpi),
    product_penetration: cell.crossSectionShareLabel ?? '-',
    final_status: cell.isWhitespace ? 'opportunity' : 'standard',
    heatmap_band: cell.heatmapColor || 'transparent',
});
